import { TutorSettingItem } from '../../../services/tutor.service';

/**
 * Scope of the chat-widget onboarding tour, configured on the agent's
 * Allgemein page below the `onboarding_tour_enabled` switch.
 *
 * Shared contract with al-tutor (template settings `onboarding_tour_topics`
 * + `onboarding_tour_max_autostarts`, bot_data in SC_INIT_CHAT) and the
 * widget's TourService: the keys below are stable ids — never rename them,
 * the labels live in i18n (`input.onboardingTour.topics.<key>`). Their order
 * is the canonical order (backend `ONBOARDING_TOUR_TOPICS`): saved values,
 * parsed values and the dropdown options always follow it.
 *
 * Welcome and finish have no topic: the widget always runs them, but does
 * not start a tour that would consist of nothing else.
 */
export const ONBOARDING_TOUR_TOPICS = [
  'chat',
  'voice',
  'lecturer',
  'web_search',
  'debate',
  'live_events',
  'learner_profile',
  'exercise_mode',
  'guided_mode',
  'tab_exercises',
  'tab_materials',
  'tab_studyplan',
  'tab_learnerprofile',
  'tab_messages',
  'tab_notifications',
  'search',
  'sidebar',
] as const;

export type OnboardingTourTopic = (typeof ONBOARDING_TOUR_TOPICS)[number];

/** One topic per widget tab (migration 205), in tab-bar order: the tab id
 *  and the topic that explains it. */
const TAB_TOPICS: { tab: string; topic: OnboardingTourTopic }[] = [
  { tab: 'exercises', topic: 'tab_exercises' },
  { tab: 'materials', topic: 'tab_materials' },
  { tab: 'studyplan', topic: 'tab_studyplan' },
  { tab: 'learnerprofile', topic: 'tab_learnerprofile' },
  { tab: 'messages', topic: 'tab_messages' },
  { tab: 'notifications', topic: 'tab_notifications' },
];

/** Retired topic ids that may still be stored (migration 204 default,
 *  values saved before 205) → the topics that replaced them. Mirror of the
 *  backend's `LEGACY_TOPIC_ALIASES`; never written back. */
const LEGACY_TOPIC_ALIASES: Record<string, readonly OnboardingTourTopic[]> = {
  tabs: TAB_TOPICS.map((t) => t.topic),
};

export const ONBOARDING_TOUR_TOPICS_VARIABLE = 'onboarding_tour_topics';
export const ONBOARDING_TOUR_MAX_AUTOSTARTS_VARIABLE =
  'onboarding_tour_max_autostarts';

export const ONBOARDING_TOUR_MAX_AUTOSTARTS_MIN = 1;
export const ONBOARDING_TOUR_MAX_AUTOSTARTS_MAX = 10;
export const ONBOARDING_TOUR_MAX_AUTOSTARTS_DEFAULT = 1;

/** What decides which topics this agent can explain at all. */
export interface OnboardingTopicContext {
  /** The agent's own settings (`fetchTutorSettings`). */
  settings: TutorSettingItem[];
  /** Template `name` of the agent, when known (only `tutor_basic` matters). */
  templateName?: string | null;
  /** An Alpha Twin (alpha_dozent) is attached via bot_dozent_link. */
  linkedTwin?: boolean;
  /** The attached Twin's settings — its tabs win while the agent's
   *  `use_twin_widget_tabs` is on (mirrors tutor_ops.py). */
  twinSettings?: TutorSettingItem[] | null;
}

/** Same truthiness as the backend's `_truthy` (tutor_ops.py, session.py)
 *  and the widget's `normalizeWidgetTabs`. */
function isTruthy(raw: unknown): boolean {
  return (
    raw === true || raw === 'true' || raw === 'True' || raw === 1 || raw === '1'
  );
}

function find(
  settings: TutorSettingItem[] | null | undefined,
  variableName: string
): TutorSettingItem | undefined {
  return (settings ?? []).find((s) => s.variable_name === variableName);
}

function effectiveValue(setting: TutorSettingItem): unknown {
  return setting.value ?? setting.default_value;
}

/** A setting that does not exist on the template counts as off. */
function settingOn(
  settings: TutorSettingItem[] | null | undefined,
  variableName: string
): boolean {
  const s = find(settings, variableName);
  return !!s && isTruthy(effectiveValue(s));
}

/** The agent shows its linked Twin's tabs (`use_twin_widget_tabs`,
 *  migration 095; missing = on, like tutor_ops.py). */
export function usesTwinWidgetTabs(settings: TutorSettingItem[]): boolean {
  const s = find(settings, 'use_twin_widget_tabs');
  return s ? isTruthy(effectiveValue(s)) : true;
}

/** Tabs the tour walks through (widget tour.service `tabSteps`: a step per
 *  tab in `enabledTabs`) — every per-tab flag of the widget. Their presence
 *  switches off the legacy `enabled_tabs` list (session.py
 *  `normalize_widget_tab_settings`, websocket `_widget_tab_payload`). */
const TOUR_TAB_FLAGS = TAB_TOPICS.map((t) => `tab_${t.tab}_enabled`);
/** Tab flags a linked Twin provides whenever its template carries them
 *  (tutor_ops.py `LINKED_DOZENT_TAB_KEYS`, session.py `TWIN_TAB_KEYS`). The
 *  Twin's legacy `tabs_enabled` / `enabled_tabs` never count (alpha_dozent,
 *  `should_ignore_legacy_dozent_tab_default`). */
const TWIN_TAB_FLAGS = [
  'tab_exercises_enabled',
  'tab_messages_enabled',
  'tab_notifications_enabled',
  'tab_materials_enabled',
];
/** Study plan + learner profile stay the agent's own — `LINKED_DOZENT_TAB_KEYS`
 *  doesn't carry them, and session.py takes them from the Twin only when the
 *  Twin has its own saved value (`TWIN_EXPLICIT_ONLY_KEYS`), never from its
 *  template default. */
const TWIN_EXPLICIT_ONLY = [
  'tab_studyplan_enabled',
  'tab_learnerprofile_enabled',
  'study_path_recommendation_enabled',
];

function parseEnabledTabs(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      /* comma list below */
    }
    return raw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

/** The tab settings the widget ends up with: the agent's own, with the
 *  linked Twin's tab flags on top while `use_twin_widget_tabs` is on. */
function tabSettingLookup(
  ctx: OnboardingTopicContext
): (variableName: string) => TutorSettingItem | undefined {
  const own = ctx.settings;
  const twin =
    ctx.linkedTwin && ctx.twinSettings?.length && usesTwinWidgetTabs(own)
      ? ctx.twinSettings
      : null;
  return (variableName) => {
    const fromTwin = twin ? find(twin, variableName) : undefined;
    if (fromTwin && TWIN_TAB_FLAGS.includes(variableName)) return fromTwin;
    if (
      fromTwin &&
      TWIN_EXPLICIT_ONLY.includes(variableName) &&
      fromTwin.value !== null &&
      fromTwin.value !== undefined
    ) {
      return fromTwin;
    }
    return find(own, variableName);
  };
}

/** The widget tabs (besides chat) the learner will see — what decides
 *  which `tab_*` topics the tour can explain. */
function availableTabs(ctx: OnboardingTopicContext): Set<string> {
  const lookup = tabSettingLookup(ctx);
  const on = (variableName: string) => {
    const s = lookup(variableName);
    return !!s && isTruthy(effectiveValue(s));
  };
  const tabs = new Set<string>();
  if (TOUR_TAB_FLAGS.some((flag) => !!lookup(flag))) {
    TAB_TOPICS.filter((t) => on(`tab_${t.tab}_enabled`)).forEach((t) =>
      tabs.add(t.tab)
    );
  } else {
    // Legacy list — no `tabs_enabled` master check: the backend derives the
    // widget's `tabs_enabled` from this list alone (`_widget_tab_payload`).
    const list = lookup('enabled_tabs');
    if (list) {
      parseEnabledTabs(effectiveValue(list)).forEach((t) => tabs.add(t));
    }
  }
  // The Lernplan feature switches the study plan + notifications tabs on by
  // itself (session.py `normalize_widget_tab_settings`).
  if (on('study_path_recommendation_enabled')) {
    tabs.add('studyplan');
    tabs.add('notifications');
  }
  return tabs;
}

/**
 * Topics this agent can explain, in canonical order. Mirrors the widget's
 * step conditions and the bot_data mapping in al-tutor `tutor_ops.py`:
 * chat, voice, search and sidebar exist for every agent; everything else
 * needs its feature switched on — a feature whose setting the template
 * does not carry counts as off.
 */
export function availableOnboardingTopics(
  ctx: OnboardingTopicContext
): OnboardingTourTopic[] {
  const own = ctx.settings;
  const tabs = availableTabs(ctx);
  const available: Record<OnboardingTourTopic, boolean> = {
    chat: true,
    voice: true,
    // Widget: dozent_bot_id && enable_lecturer_routing — a linked Twin
    // forces the routing on, without a Twin the step never shows.
    lecturer: !!ctx.linkedTwin,
    web_search: settingOn(own, 'search_web'),
    debate: settingOn(own, 'debate_mode_enabled'),
    live_events: settingOn(own, 'live_actions'),
    learner_profile: settingOn(own, 'track_learning_progress'),
    // tutor_basic predates the `exercise_mode` setting and has it always on.
    exercise_mode: find(own, 'exercise_mode')
      ? settingOn(own, 'exercise_mode')
      : ctx.templateName === 'tutor_basic',
    guided_mode: settingOn(own, 'guided_mode_enabled'),
    tab_exercises: tabs.has('exercises'),
    tab_materials: tabs.has('materials'),
    tab_studyplan: tabs.has('studyplan'),
    tab_learnerprofile: tabs.has('learnerprofile'),
    tab_messages: tabs.has('messages'),
    tab_notifications: tabs.has('notifications'),
    search: true,
    sidebar: true,
  };
  return ONBOARDING_TOUR_TOPICS.filter((t) => available[t]);
}

/** A JSON string holding the array may itself be JSON-encoded again (older
 *  save paths); unwrap at most this many string layers — as the backend. */
const MAX_STRING_LAYERS = 3;

/** The flat-list part of Python's `ast.literal_eval` that a stored list repr
 *  (`"['chat', 'voice']"`, `"()"`) needs: lists/tuples (nested too) of
 *  strings, numbers, None/True/False. `undefined` for anything else. */
function parsePythonListLiteral(text: string): unknown[] | undefined {
  let i = 0;
  const fail = new Error('not a literal');
  const skipSpace = () => {
    while (i < text.length && /\s/.test(text[i])) i++;
  };
  const value = (): unknown => {
    skipSpace();
    const c = text[i];
    if (c === '[' || c === '(') {
      const close = c === '[' ? ']' : ')';
      const items: unknown[] = [];
      let trailingComma = false;
      i++;
      skipSpace();
      while (text[i] !== close) {
        items.push(value());
        trailingComma = false;
        skipSpace();
        if (text[i] === ',') {
          i++;
          skipSpace();
          trailingComma = true;
        } else if (text[i] !== close) {
          throw fail;
        }
      }
      i++;
      // `(x)` is x in parentheses, not a one-element tuple.
      return c === '(' && items.length === 1 && !trailingComma
        ? items[0]
        : items;
    }
    if (c === "'" || c === '"') {
      let out = '';
      i++;
      while (i < text.length && text[i] !== c) {
        if (text[i] === '\\') i++;
        out += text[i++] ?? '';
      }
      if (i >= text.length) throw fail;
      i++;
      return out;
    }
    const m =
      /^(?:None|True|False|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)(?!\w)/.exec(
        text.slice(i)
      );
    if (!m) throw fail;
    i += m[0].length;
    if (m[0] === 'None') return null;
    if (m[0] === 'True' || m[0] === 'False') return m[0] === 'True';
    return Number(m[0]);
  };
  try {
    const result = value();
    skipSpace();
    return i === text.length && Array.isArray(result) ? result : undefined;
  } catch {
    return undefined;
  }
}

/** Stored multi-select value → plain list, or `null` when unreadable.
 *  Mirror of the backend's `_decode_list` (onboarding_tour_settings.py):
 *  array, JSON array, JSON string holding one of these (≤ 3 layers), Python
 *  list repr, comma list. Blank strings and non-list JSON → `null`. */
function decodeTopicList(raw: unknown): unknown[] | null {
  let current = raw;
  for (let layer = 0; layer <= MAX_STRING_LAYERS; layer++) {
    if (Array.isArray(current)) return current;
    if (typeof current !== 'string') return null;
    let text = current.trim();
    if (!text) return null;
    let decoded: unknown;
    let isJson = true;
    try {
      decoded = JSON.parse(text);
    } catch {
      isJson = false;
    }
    if (isJson) {
      if (Array.isArray(decoded) || typeof decoded === 'string') {
        current = decoded;
        continue;
      }
      return null;
    }
    if (text[0] === '[' || text[0] === '(') {
      const literal = parsePythonListLiteral(text);
      if (literal) return literal;
      text = text.replace(/^[\[\]()]+|[\[\]()]+$/g, '');
    }
    return text
      .split(',')
      .map((part) => part.trim().replace(/^['"]+|['"]+$/g, ''));
  }
  return null;
}

/**
 * Stored topic list → known keys (trimmed, lower-cased), duplicates dropped,
 * in canonical order — never the stored order. Mirror of the backend's
 * `parse_onboarding_tour_topics`:
 *   - unset / unreadable → `null`, the contract's "all topics";
 *   - empty list → `[]`, nothing selected;
 *   - non-empty list without a single known key → `null` (a value we don't
 *     understand, not a deliberate "nothing");
 *   - the retired `tabs` stands for every tab topic.
 */
export function parseOnboardingTopics(
  raw: unknown
): OnboardingTourTopic[] | null {
  if (raw === null || raw === undefined) return null;
  const items = decodeTopicList(raw);
  if (!items) return null;
  if (!items.length) return [];
  const picked = new Set(
    items
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean)
  );
  for (const [legacy, topics] of Object.entries(LEGACY_TOPIC_ALIASES)) {
    if (picked.has(legacy)) topics.forEach((t) => picked.add(t));
  }
  const topics = ONBOARDING_TOUR_TOPICS.filter((t) => picked.has(t));
  return topics.length ? topics : null;
}

/** The saved selection with "unset" resolved to every topic. */
export function savedOnboardingTopics(
  setting: TutorSettingItem | undefined
): OnboardingTourTopic[] {
  const parsed = setting ? parseOnboardingTopics(effectiveValue(setting)) : null;
  return parsed ?? [...ONBOARDING_TOUR_TOPICS];
}

/** What the dropdown shows: the saved topics this agent can explain, in
 *  canonical order. */
export function visibleOnboardingTopicSelection(
  setting: TutorSettingItem | undefined,
  available: readonly OnboardingTourTopic[]
): OnboardingTourTopic[] {
  const saved = savedOnboardingTopics(setting);
  return ONBOARDING_TOUR_TOPICS.filter(
    (t) => available.includes(t) && saved.includes(t)
  );
}

/** At least one topic the agent can explain must stay selected —
 *  otherwise only welcome + finish would remain and the tour never starts. */
export function canSaveOnboardingTopics(
  selected: readonly string[] | null | undefined,
  available: readonly OnboardingTourTopic[]
): boolean {
  return (selected ?? []).some((t) =>
    available.includes(t as OnboardingTourTopic)
  );
}

/**
 * Value to save, in canonical order: the dropdown decides the topics it
 * shows; topics it hides (feature currently off) keep their saved state, so
 * switching a feature back on later brings its explanation back as it was.
 */
export function mergeOnboardingTopics(
  selected: readonly string[] | null | undefined,
  setting: TutorSettingItem | undefined,
  available: readonly OnboardingTourTopic[]
): OnboardingTourTopic[] {
  const picked = new Set(selected ?? []);
  const saved = savedOnboardingTopics(setting);
  return ONBOARDING_TOUR_TOPICS.filter((t) =>
    available.includes(t) ? picked.has(t) : saved.includes(t)
  );
}

/** Whole number in 1..10; anything unreadable falls back to 1. */
export function clampOnboardingMaxAutostarts(raw: unknown): number {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim()
        ? Number(raw)
        : NaN;
  if (!Number.isFinite(n)) return ONBOARDING_TOUR_MAX_AUTOSTARTS_DEFAULT;
  return Math.min(
    ONBOARDING_TOUR_MAX_AUTOSTARTS_MAX,
    Math.max(ONBOARDING_TOUR_MAX_AUTOSTARTS_MIN, Math.round(n))
  );
}
